import React, { useRef, useState } from 'react';
import { Redirect, useHistory } from 'react-router-dom';

import {
  DividerCard,
  CardStackSection,
  Box,
  Heading,
  Text,
  Button,
  Divider,
  MarkdownLite,
  Stack,
} from '@origin-digital/ods-core';
import { formatCurrency } from '@origin-digital/formatters';
import { PaymentInformationCard } from '@origin-digital/payments';
import { navigationBack } from '@origin-digital/event-dispatcher';
import { logger, reportException } from '@origin-digital/reporting-client';
import { Recaptcha, ReCAPTCHA, RecaptchaDisclaimer } from '@origin-digital/recaptcha';

import { config } from 'src/config';
import { routes } from 'src/consts';
import { useOnce } from 'src/hooks';
import { useAppContext, IContext } from 'src/context/AppContext';
import { useSharedAppContext } from 'src/context/SharedAppContext';
import { AccountBackend } from 'src/interfaces';
import { Form } from 'src/components/Form';
import { appendInvalidRedirectUrl, getPaymentCardType, isMyAccount } from 'src/utils/helpers';
import { Loading } from 'src/components/Loading';
import { ContractInformation } from 'src/components/ContractInformation';
import { makePayment } from 'src/utils/payments';
import { formComplete } from 'src/utils/tracking';
import {
  showErrorNotification,
  hideNotification,
  showInfoNotification,
} from 'src/utils/notification';

import * as content from './content.json';

export const getErrorMessage = (error: Error, { paymentMethod }: IContext): string => {
  const { errors } = content.form;

  const errorContent = errors[error.message];

  if (errorContent) {
    if (paymentMethod) {
      return errorContent.replace('{cardId}', paymentMethod.paymentCardId);
    }
    return errorContent;
  }

  return errors.generic;
};

export const ConfirmScreen: React.SFC = () => {
  const { context, setContext } = useAppContext();
  const sharedAppContext = useSharedAppContext();
  const history = useHistory();
  const [redirecting, setRedirecting] = useState<boolean>(false);
  const [validating, setValidating] = useState<boolean>(false);
  const [mounting, setMounting] = useState<boolean>(true);
  const [invalid, setInvalid] = useState<boolean>(false);
  const [hasKrakenPaymentError, setHasKrakenPaymentError] = useState<boolean>(false);
  const [hasKrakenAccountError, setHasKrakenAccountError] = useState<boolean>(false);
  const recaptchaRef = useRef<ReCAPTCHA>();

  const { amount, amountDue, contractAccountId, divisionId, paymentData, paymentMethod } = context;

  const shouldRecaptcha = () => !isMyAccount();
  const isKrakenAccount = context.backend === AccountBackend.kraken;

  useOnce(() => {
    setMounting(false);

    if (!contractAccountId) {
      logger.info('confirmScreen.setInvalid(): contractAccountId');
      return setInvalid(true);
    }

    if (!paymentMethod && !paymentData) {
      logger.info('confirmScreen.setInvalid(): paymentMethod | paymentData');
      return setInvalid(true);
    }
  });

  const handleSubmit = async (recaptchaToken?: string) => {
    showInfoNotification(content.processing);

    try {
      const { receiptNumber, timestamp } = await makePayment(context, recaptchaToken);

      formComplete(
        {
          amount,
          amountDue,
          divisionId,
          receiptNumber,
        },
        sharedAppContext,
      );

      setContext({
        receiptNumber,
        timestamp,
      });

      setValidating(false);
      setRedirecting(true);
    } catch (error) {
      const invalidKrakenAccount = error.message === 'invalid-kraken-account';
      if (invalidKrakenAccount) {
        const errorMessage = content.form.errors.invalidKrakenAccount;
        showErrorNotification(errorMessage.message, errorMessage.heading);
        setHasKrakenAccountError(true);
      } else if (isKrakenAccount) {
        showErrorNotification(content.form.errors.kraken);
        setHasKrakenPaymentError(true);
      } else {
        showErrorNotification(getErrorMessage(error, context));
      }

      reportException('confirmScreen.handleSubmit()', error);
      setValidating(false);
    }
  };

  const verifyRecaptcha = () => {
    setValidating(true);
    hideNotification();

    if (!shouldRecaptcha()) {
      return handleSubmit();
    }

    const recaptchaValue = recaptchaRef.current.getValue();
    if (recaptchaValue) {
      return handleSubmit(recaptchaValue);
    }

    recaptchaRef.current.execute();
  };

  if (mounting) {
    return <Loading />;
  }

  if (redirecting) {
    return (
      <div data-id="redirect-success">
        <Redirect to={routes.success} push />
      </div>
    );
  }

  if (invalid) {
    return (
      <div data-id="redirect-invalid">
        <Redirect to={appendInvalidRedirectUrl('/')} />
      </div>
    );
  }

  const goBack = hasKrakenAccountError
    ? () => {
        history.push(routes.selectContractAccount);
      }
    : navigationBack;

  return (
    <CardStackSection data-id="confirm-payment">
      <DividerCard space={['large', 'xlarge']}>
        <ContractInformation
          address={context.address}
          divisionId={context.divisionId}
          contractAccountId={context.contractAccountId}
        />

        <Form onSubmit={verifyRecaptcha}>
          <Stack space={['large', 'xlarge']}>
            <Heading variant="h4">{content.heading}</Heading>

            <Stack space="small">
              <Text tone="neutralLight" variant="body-small">
                {content.labels.amount}
              </Text>

              <Heading variant="h2" data-id="amount">
                {formatCurrency(parseFloat(amount.replace(/,/g, '')))}
              </Heading>
            </Stack>

            <Divider />

            <Stack space="small">
              <Text tone="neutralLight" variant="body-small">
                {content.labels.card}
              </Text>

              <Box>
                {paymentMethod ? (
                  <PaymentInformationCard
                    cardNumber={paymentMethod.cardNumber}
                    validTo={paymentMethod.validTo}
                    color="grey56"
                    paymentCardTypeId={paymentMethod.paymentCardTypeId}
                  />
                ) : (
                  <PaymentInformationCard
                    cardNumber={paymentData.cardNumber}
                    validTo={paymentData.expiryDate}
                    color="grey56"
                    paymentCardTypeId={getPaymentCardType(paymentData.cardNumber)}
                  />
                )}

                {shouldRecaptcha() && (
                  <Recaptcha
                    onClose={() => setValidating(false)}
                    onSuccess={handleSubmit}
                    recaptchaRef={recaptchaRef}
                    recaptchaKey={config.recaptchaKey}
                  />
                )}
              </Box>
            </Stack>

            <Divider />

            <Stack space={['large', 'xlarge']}>
              {!isKrakenAccount && (
                <Text data-id="disclaimer">
                  <MarkdownLite>{content.disclaimer}</MarkdownLite>
                </Text>
              )}

              <Button
                type="submit"
                data-id="continue-button"
                spinner={validating}
                disabled={hasKrakenPaymentError || hasKrakenAccountError}
                fullWidth
              >
                {content.form.button}
              </Button>

              <Stack alignX="center">
                <Button
                  size="small"
                  variant="text"
                  noTextPadding
                  onClick={goBack}
                  data-id="back-button"
                  disabled={validating || hasKrakenPaymentError}
                >
                  {content.form.change[hasKrakenAccountError ? 'account' : 'payment']}
                </Button>
              </Stack>
            </Stack>

            {shouldRecaptcha() && <RecaptchaDisclaimer />}
          </Stack>
        </Form>
      </DividerCard>
    </CardStackSection>
  );
};
